import { useCallback, useRef, useState } from "react";
import { observer } from "mobx-react-lite";

import { useStores } from "stores";
import { CustomButton } from "ui";
import { getContractBidsReport, getReportById, NormalizedError } from "services";
import { useNotificationContext } from "providers";
import { download, parseErrorMessage } from "utils";
import { NOTIFICATION_TYPE_ENUM } from "interfaces";
import { ReactComponent as IconDownload } from "images/downloadIcon.svg";

export const ReportOfBidsButton = observer(() => {
  const { contractCard } = useStores();
  const { openNotification } = useNotificationContext();
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  const [loading, setLoading] = useState(false);

  const clickHandler = async () => {
    setLoading(true);

    try {
      const reportId = await getContractBidsReport({
        contractId: contractCard.card.id,
      });
      if (reportId) {
        getFileById(reportId);
      } else {
        setLoading(false);
      }
    } catch (e: unknown) {
      openNotification({
        type: NOTIFICATION_TYPE_ENUM.ERROR,
        description: parseErrorMessage(e as NormalizedError),
      });
      setLoading(false);
    }
  };

  const clearPollingStatus = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    setLoading(false);
  }, []);

  const getFileById = useCallback(
    (id: string) => {
      if (intervalRef.current) clearInterval(intervalRef.current);

      intervalRef.current = setInterval(async () => {
        try {
          const data = await getReportById({ id });

          if (data.errorMessage) {
            openNotification({
              description: data.errorMessage,
              type: NOTIFICATION_TYPE_ENUM.ERROR,
            });
            clearPollingStatus();
          } else {
            if (data.doc) {
              download(data.doc.url, data.doc.docName);
              clearPollingStatus();
            }
          }
        } catch (e: any) {
          if (e.status !== 404) {
            clearPollingStatus();
            openNotification({
              description: parseErrorMessage(e),
              type: NOTIFICATION_TYPE_ENUM.ERROR,
            });
          }
        }
      }, 3000);
    },
    [openNotification, clearPollingStatus]
  );

  return (
    <CustomButton
      type="text"
      size="middle"
      onClick={clickHandler}
      tooltip="Выгрузить реестр заявок по договору"
      loading={loading}
      disabled={loading}
      icon={<IconDownload />}
    >
      Скачать
    </CustomButton>
  );
});
